import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "./api";
import { ApiError } from "./incident-api";

export { ApiError };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return data as T;
}

export const ASSET_CATEGORIES = [
  "laptop",
  "phone",
  "vehicle",
  "keys",
  "uniform",
  "equipment",
  "other",
] as const;
/**
 * An item's lifecycle / condition, independent of who holds it. This is the
 * only status a user sets by hand. Availability (`available` / `assigned` /
 * `returned`) is derived from the assignment by the server, never edited here.
 */
export const ASSET_LIFECYCLES = [
  "in_service",
  "maintenance",
  "damaged",
  "lost",
  "retired",
] as const;
export const ASSET_AVAILABILITIES = ["available", "assigned", "returned"] as const;

export type AssetCategory = (typeof ASSET_CATEGORIES)[number];
export type AssetLifecycle = (typeof ASSET_LIFECYCLES)[number];
export type AssetAvailability = (typeof ASSET_AVAILABILITIES)[number];

/**
 * Coerce any stored lifecycle string to a valid one, defaulting to "in_service".
 * Guards against pre-split legacy values ("available" / "assigned" / "returned")
 * that may still linger in rows until the 0050 backfill runs — feeding one of
 * those into the unit form's `z.enum` would otherwise throw "Invalid option".
 */
export function coerceLifecycle(value: string | null | undefined): AssetLifecycle {
  return (ASSET_LIFECYCLES as readonly string[]).includes(value ?? "")
    ? (value as AssetLifecycle)
    : "in_service";
}

/** Human label for a lifecycle value (e.g. "in_service" → "In service"). */
export const LIFECYCLE_LABELS: Record<AssetLifecycle, string> = {
  in_service: "In service",
  maintenance: "Maintenance",
  damaged: "Damaged",
  lost: "Lost",
  retired: "Retired",
};

/** Aggregate unit stats returned with each property in the list. */
export type PropertyStats = {
  unitCount: number;
  totalQty: number;
  assignedQty: number;
};

export type AssetProperty = {
  id: string;
  name: string;
  category: AssetCategory;
  serialTracked: boolean;
  quantity: number;
  imageUrl: string | null;
  notes: string | null;
  recordedBy: string | null;
  createdAt: string;
  updatedAt: string;
  stats?: PropertyStats;
};

export type AssetUnit = {
  id: string;
  propertyId: string;
  serialNumber: string | null;
  quantity: number;
  lifecycle: AssetLifecycle;
  /** Derived server-side from the assignment — read-only on the client. */
  availability: AssetAvailability;
  condition: string | null;
  value: string | null;
  imageUrl: string | null;
  assignedTo: string | null;
  assignedDate: string | null;
  returnedDate: string | null;
  notes: string | null;
  recordedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

/** Lightweight property summary carried alongside an assigned unit. */
export type AssignmentProperty = {
  id: string;
  name: string;
  category: AssetCategory;
  serialTracked: boolean;
  imageUrl: string | null;
};

/** An assigned unit paired with its parent property (for rosters / profiles). */
export type Assignment = AssetUnit & { property: AssignmentProperty };

export type PropertyInput = {
  name: string;
  category: AssetCategory;
  serialTracked: boolean;
  quantity: number;
  notes?: string;
};

export type UnitInput = {
  serialNumber?: string;
  quantity: number;
  lifecycle: AssetLifecycle;
  condition?: string;
  value?: string;
  assignedTo?: string | null;
  assignedDate?: string;
  returnedDate?: string;
  notes?: string;
  // When true and this results in an assignment, email the employee.
  notify?: boolean;
};

export type UnitEventAction =
  | "created"
  | "assigned"
  | "reassigned"
  | "returned"
  | "status_changed"
  | "notified"
  | "acknowledged"
  | "issue_reported"
  | "issue_status_changed"
  | "issue_resolved"
  | "issue_reopened";

export type AssetUnitEvent = {
  id: string;
  unitId: string;
  action: UnitEventAction;
  fromEmployee: string | null;
  toEmployee: string | null;
  fromEmployeeName: string | null;
  toEmployeeName: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  note: string | null;
  actorUserId: string | null;
  actorName: string | null;
  issueId?: string | null;
  createdAt: string;
};

/* ── Properties ──────────────────────────────────────────────────────────── */

export async function listProperties(): Promise<AssetProperty[]> {
  const data = await request<{ items: AssetProperty[] }>("/api/asset-properties");
  return data.items;
}

/** Filters + paging for the property catalog (server-side). */
export type PropertiesPageParams = {
  limit: number;
  offset: number;
  q?: string;
  category?: AssetCategory;
  sort?: "name" | "category" | "tracking";
  dir?: "asc" | "desc";
};

/** One page of properties plus the overall total for the active filters. */
export type PropertiesPage = { items: AssetProperty[]; total: number };

/** A filtered, paginated slice of the property catalog (`/api/asset-properties/page`). */
export async function fetchPropertiesPage(params: PropertiesPageParams): Promise<PropertiesPage> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.q) search.set("q", params.q);
  if (params.category) search.set("category", params.category);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  return request<PropertiesPage>(`/api/asset-properties/page?${search.toString()}`);
}

export async function getProperty(
  id: string,
): Promise<{ property: AssetProperty; units: AssetUnit[] }> {
  const data = await request<{ item: AssetProperty; units: AssetUnit[] }>(
    `/api/asset-properties/${id}`,
  );
  return { property: data.item, units: data.units };
}

export async function createProperty(input: PropertyInput): Promise<AssetProperty> {
  const data = await request<{ item: AssetProperty }>("/api/asset-properties", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.item;
}

export async function updateProperty(
  id: string,
  patch: Partial<PropertyInput>,
): Promise<AssetProperty> {
  const data = await request<{ item: AssetProperty }>(`/api/asset-properties/${id}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  return data.item;
}

export async function deleteProperty(id: string): Promise<void> {
  await request<void>(`/api/asset-properties/${id}`, { method: "DELETE" });
}

/* ── Units ───────────────────────────────────────────────────────────────── */

export async function createUnit(propertyId: string, input: UnitInput): Promise<AssetUnit> {
  const data = await request<{ item: AssetUnit }>(`/api/asset-properties/${propertyId}/units`, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.item;
}

export async function createUnitsBulk(
  propertyId: string,
  units: UnitInput[],
): Promise<AssetUnit[]> {
  const data = await request<{ items: AssetUnit[] }>(
    `/api/asset-properties/${propertyId}/units/bulk`,
    { method: "POST", body: JSON.stringify({ units }) },
  );
  return data.items;
}

export async function updateUnit(
  propertyId: string,
  unitId: string,
  patch: Partial<UnitInput>,
): Promise<AssetUnit> {
  const data = await request<{ item: AssetUnit }>(
    `/api/asset-properties/${propertyId}/units/${unitId}`,
    { method: "PUT", body: JSON.stringify(patch) },
  );
  return data.item;
}

/**
 * Return an assigned item to the pool in one step: clears the assignee, stamps
 * the returned date and optionally records its condition on the way back.
 */
export async function returnUnit(
  propertyId: string,
  unitId: string,
  input: { returnedDate?: string; condition?: string } = {},
): Promise<AssetUnit> {
  const data = await request<{ item: AssetUnit }>(
    `/api/asset-properties/${propertyId}/units/${unitId}/return`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return data.item;
}

export async function deleteUnit(propertyId: string, unitId: string): Promise<void> {
  await request<void>(`/api/asset-properties/${propertyId}/units/${unitId}`, {
    method: "DELETE",
  });
}

export async function listUnitEvents(
  propertyId: string,
  unitId: string,
): Promise<AssetUnitEvent[]> {
  const data = await request<{ items: AssetUnitEvent[] }>(
    `/api/asset-properties/${propertyId}/units/${unitId}/events`,
  );
  return data.items;
}

/* ── Images ──────────────────────────────────────────────────────────────── */

/** Uploads an image (multipart) to `path` and returns the parsed `{ item }`. */
async function uploadImage<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      method: "POST",
      credentials: "include",
      // No Content-Type — the browser sets the multipart boundary.
      body: form,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Upload failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return (data as { item: T }).item;
}

export function uploadPropertyImage(propertyId: string, file: File): Promise<AssetProperty> {
  return uploadImage<AssetProperty>(`/api/asset-properties/${propertyId}/image`, file);
}

export async function deletePropertyImage(propertyId: string): Promise<AssetProperty> {
  const data = await request<{ item: AssetProperty }>(`/api/asset-properties/${propertyId}/image`, {
    method: "DELETE",
  });
  return data.item;
}

export function uploadUnitImage(
  propertyId: string,
  unitId: string,
  file: File,
): Promise<AssetUnit> {
  return uploadImage<AssetUnit>(`/api/asset-properties/${propertyId}/units/${unitId}/image`, file);
}

export async function deleteUnitImage(propertyId: string, unitId: string): Promise<AssetUnit> {
  const data = await request<{ item: AssetUnit }>(
    `/api/asset-properties/${propertyId}/units/${unitId}/image`,
    { method: "DELETE" },
  );
  return data.item;
}

/* ── Assignments ─────────────────────────────────────────────────────────── */

/**
 * Active assignments across the workspace, or just one employee's holdings when
 * `employeeId` is given. Each item is an assigned unit plus its property.
 */
export async function listAssignments(employeeId?: string): Promise<Assignment[]> {
  const qs = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : "";
  const data = await request<{ items: Assignment[] }>(`/api/asset-properties/assignments${qs}`);
  return data.items;
}
